import React from 'react';
import { Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { toHumanReadableDate } from '@shared/services/dateService';
import INIT_DRAFT_CHECKPOINT from '../queries/initDraftCheckPointMutation';
import ApolloError from '@shared/components/ApolloError';
import { useMutation } from '@apollo/client';
import CheckPointList from './CheckPointList';

/**
 * @typedef {object} AppraisalCardsProps
 * @property {number} [orgUnitId]
 * @property {Array<*>} [appraisalOrgUnits]
 * @property {boolean} loading
 */

/**
 * @param {AppraisalCardsProps} props
 */
const AppraisalCards = props => {
  const { orgUnitId, appraisalOrgUnits, loading } = props;

  const navigate = useNavigate();
  const [initDraftCheckPoint, { error: initDraftCheckPointError }] =
    useMutation(INIT_DRAFT_CHECKPOINT);

  const cardsData = loading ? dummyCardsData : appraisalOrgUnits;

  const handleAddCheckPoint = async appraisalOrgUnit => {
    const appraisalId = appraisalOrgUnit.Appraisal.Id;

    const draftCheckPointData = await initDraftCheckPoint({
      variables: {
        orgUnitId: orgUnitId,
        appraisalId: appraisalId
      }
    });

    navigateToAddCheckPoint(appraisalId, draftCheckPointData.data.initDraftCheckPoint);
  };

  const navigateToAddCheckPoint = (appraisalId, checkpointId) => {
    navigate(`/check-points/${orgUnitId}/add-check-point/${appraisalId}/${checkpointId}`);
  };

  if (initDraftCheckPointError) {
    return <ApolloError />;
  }

  return (
    <div className='appraisal-cards' data-testid='appraisal-cards'>
      {cardsData.map(appraisalOrgUnit => (
        <Card key={appraisalOrgUnit.Appraisal.Id}>
          <SkeletonWrapper show={loading}>
            <CardContent>
              <h4 className='appraisal-tab-name'>Appraisal {appraisalOrgUnit.Appraisal.Id}</h4>
              <h3 className='appraisal-date'>
                {toHumanReadableDate(appraisalOrgUnit.Appraisal.ConductAppraisalEndDate)}
              </h3>
              {appraisalOrgUnit.CheckPoints?.length > 0 ? (
                <CheckPointList
                  orgUnitId={orgUnitId}
                  appraisalOrgUnit={appraisalOrgUnit}
                  navigateToAddCheckPoint={navigateToAddCheckPoint}
                />
              ) : (
                <div className='no-check-points'>
                  <span>No Check Points Added</span>
                </div>
              )}
              <button
                className='add-check-point'
                onClick={() => handleAddCheckPoint(appraisalOrgUnit)}>
                Add Check Point
              </button>
            </CardContent>
          </SkeletonWrapper>
        </Card>
      ))}
    </div>
  );
};

const dummyCardsData = Array.from({ length: 2 }, (_, index) => ({
  Appraisal: {
    Id: index + 1
  }
}));

export default AppraisalCards;
